// Copyright (C) 2020-2022 Intel Corporation
// Copyright (C) 2022-2023 CVAT.ai Corporation
//
// SPDX-License-Identifier: MIT

import { AnyAction } from 'redux';
import { BoundariesActionTypes } from 'actions/boundaries-actions';
import { ReferencesActionTypes } from 'actions/references-actions';
import { AuthActionTypes } from 'actions/auth-actions';

import { AnnotationActionTypes } from 'actions/annotation-actions';
import { ReferencesState } from '.';

const defaultState: ReferencesState = {
    initialized: false,
    fetching: false,
    moveReference: {
        modalVisible: false,
        referenceId: null,
    },
    count: 0,
    current: [],
    previews: {},
    gettingQuery: {
        page: 1,
        id: null,
        search: null,
        filter: null,
        sort: null,
        projectId: null,
        taskId: null
    },
    activities: {
        deletes: {},
    },
};

export default (state: ReferencesState = defaultState, action: AnyAction): ReferencesState => {
    switch (action.type) {
        case ReferencesActionTypes.GET_REFERENCES:
            return {
                ...state,
                activities: {
                    ...state.activities,
                    deletes: {},
                },
                initialized: false,
                fetching: true,
                count: 0,
                gettingQuery: action.payload.updateQuery ? {
                    ...defaultState.gettingQuery,
                    ...action.payload.query,
                } : state.gettingQuery,
            };
        case ReferencesActionTypes.GET_REFERENCES_SUCCESS: {
            return {
                ...state,
                initialized: true,
                fetching: false,
                count: action.payload.count,
                current: action.payload.array,
            };
        }
        case ReferencesActionTypes.UPDATE_REFERENCE_IN_STATE: {
            const { reference } = action.payload;
            return {
                ...state,
                current: state.current.map((referenceInstance) => {
                    if (referenceInstance.id === reference.id) {
                        return reference;
                    }
                    return referenceInstance;
                }),
            };
        }
        case ReferencesActionTypes.GET_REFERENCES_FAILED:
            return {
                ...state,
                initialized: true,
                fetching: false,
            };
        case ReferencesActionTypes.DELETE_REFERENCE: {
            const { referenceID } = action.payload;
            const { deletes } = state.activities;

            deletes[referenceID] = false;

            return {
                ...state,
                activities: {
                    ...state.activities,
                    deletes: {
                        ...deletes,
                    },
                },
            };
        }
        case ReferencesActionTypes.DELETE_REFERENCE_SUCCESS: {
            const { referenceID } = action.payload;
            const { deletes } = state.activities;

            deletes[referenceID] = true;

            return {
                ...state,
                activities: {
                    ...state.activities,
                    deletes: {
                        ...deletes,
                    },
                },
            };
        }
        case ReferencesActionTypes.DELETE_REFERENCE_FAILED: {
            const { referenceID } = action.payload;
            const { deletes } = state.activities;

            delete deletes[referenceID];

            return {
                ...state,
                activities: {
                    ...state.activities,
                    deletes: {
                        ...deletes,
                    },
                },
            };
        }
        case ReferencesActionTypes.SWITCH_MOVE_REFERENCE_MODAL_VISIBLE: {
            return {
                ...state,
                moveReference: {
                    ...state.moveReference,
                    modalVisible: action.payload.visible,
                    referenceId: action.payload.referenceId,
                },
            };
        }
        case AnnotationActionTypes.CLOSE_JOB: {
            return {
                ...state,
            };
        }
        case BoundariesActionTypes.RESET_AFTER_ERROR:
        case AuthActionTypes.LOGOUT_SUCCESS: {
            return { ...defaultState };
        }
        case ReferencesActionTypes.GET_REFERENCE_PREVIEW: {
            const { referenceID } = action.payload;
            const { previews } = state;
            return {
                ...state,
                previews: {
                    ...previews,
                    [referenceID]: {
                        preview: '',
                        fetching: true,
                        initialized: false,
                    },
                },
            };
        }
        case ReferencesActionTypes.GET_REFERENCE_PREVIEW_SUCCESS: {
            const { referenceID, preview } = action.payload;
            const { previews } = state;
            return {
                ...state,
                previews: {
                    ...previews,
                    [referenceID]: {
                        preview,
                        fetching: false,
                        initialized: true,
                    },
                },
            };
        }
        case ReferencesActionTypes.GET_REFERENCE_PREVIEW_FAILED: {
            const { referenceID } = action.payload;
            const { previews } = state;
            return {
                ...state,
                previews: {
                    ...previews,
                    [referenceID]: {
                        ...previews[referenceID],
                        fetching: false,
                        initialized: true,
                    },
                },
            };
        }
        case ReferencesActionTypes.LINK_TASK_TO_REFERENCE: {
            const { referenceID, taskIds } = action.payload;

            const updatedReference = {
                ...state.current[referenceID],
                taskIds: [...taskIds],
            };

            const updatedCurrent = [
                ...state.current.slice(0, referenceID),
                updatedReference,
                ...state.current.slice(referenceID + 1),
            ];

            return {
                ...state,
                current: updatedCurrent,
            };
        }
        default:
            return state;
    }
};
